import { ApolloClient, InMemoryCache, split, from } from '@apollo/client';
import { getMainDefinition, offsetLimitPagination } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { errorLinkMonitoring } from '@bloomays-lib/common.monitoring/browser';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import type { AuthBloomaysContext } from '@bloomays-lib/types.shared';

import { ApolloClientConfig } from '../types';
import { createAuthLink } from './links/createAuthLink';

export const apolloClientFactory = (
  getAccessTokenSilently: AuthBloomaysContext['getAccessTokenSilently'],
  config: ApolloClientConfig,
) => {
  const { apiUrls, filterExceptions } = config;
  const url = new URL(apiUrls.bloomers || 'localhost:4000');
  const secure = url.protocol.includes('https');

  const wsLinkBloomer = new GraphQLWsLink(
    createClient({
      url: `ws${secure ? 's' : ''}://${url.host}/subscriptions`,
    }),
  );

  const httpLinkBloomers = createUploadLink({
    uri: apiUrls.bloomers,
    credentials: 'include',
  });

  const httpLinkTalents = createUploadLink({
    uri: `${apiUrls.talents}graphql`,
    credentials: 'include',
  });

  const authLink = createAuthLink(getAccessTokenSilently, config.logger);

  const multiLinkBloomer = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLinkBloomer,
    httpLinkBloomers,
  );

  const monitLink = errorLinkMonitoring({ filterExceptions: filterExceptions || (() => true) });

  return new ApolloClient({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    link: split(
      (operation) => {
        return operation.getContext().clientName === 'api.talents';
      },
      from([monitLink, authLink, httpLinkTalents]), //if above
      from([monitLink, authLink, multiLinkBloomer]),
    ),
    cache: new InMemoryCache({
      typePolicies: config.typePolicies || {
        Query: {
          fields: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            allMissions: { missions: offsetLimitPagination(['isActive']) },
            me: {
              // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
              merge: true,
            },
          },
        },
      },
    }),
  });
};
